import { FormControl } from '@mui/material';
import { DatePicker, DateValidationError, DateView } from '@mui/x-date-pickers';
import { useReferredState } from 'hooks/useReferredState';
import moment, { Moment } from 'moment';
import React, { useEffect, useState } from 'react';

////////////////////////////////////////////////////////////////////////////

export type TimePartHour = "initialDate" | "endDate";

interface IProps {
    autoFocus?: boolean;
    value: Date | null;
    fullWidth?: boolean;
    clearable?: boolean;
    width?: string | number;
    margin?: MuiMargin;
    timePartHour?: TimePartHour;
    format?: string;
    label?: string;
    title?: string;
    placeholder?: string;
    disabled?: boolean;
    maxDate?: Date | null;
    minDate?: Date | null;
    error?: boolean;
    helperText?: React.ReactNode;
    required?: boolean;
    disablePast?: boolean;
    color?: MprcColors;
    views?: readonly DateView[];

    onChange: (date: Date | null, valid: boolean) => void;
    onError?: (error: DateValidationError, value: Date | null) => void;
    onBlur?: () => void;
}

////////////////////////////////////////////////////////////////////////////

export function formatDateTime(date: Date, timePartHour: TimePartHour = "initialDate") {
    if (timePartHour == "initialDate") {
        date.setHours(0);
        date.setMinutes(0);
        date.setSeconds(0);
    }
    else {
        date.setHours(23);
        date.setMinutes(59);
        date.setSeconds(59);
    }
    return date;
}

////////////////////////////////////////////////////////////////////////////

export const MprcDatePicker = (props: IProps) => {
    const {
        fullWidth, width = "140px", margin, autoFocus, disablePast, required, value, minDate, maxDate,
        placeholder, disabled, error, helperText, label, title, timePartHour, color, views, format, clearable,
        onChange, onError, onBlur
    } = props;

    const [date, setDate, refDate] = useReferredState<Moment | null>(value == null ? null : moment(value));
    const [valid, setValid] = useState<boolean>(true);

    ///
    const updateValue = (dtMoment: Moment | null) => {
        const isValid = dtMoment === null || (dtMoment.isValid() && dtMoment.year() >= 1970);
        onChange(
            refDate.current == null ? null : formatDateTime(refDate.current.toDate(), timePartHour)
            , isValid
        );
    };

    ///
    const handleUpdateDate = (dtMoment: Moment | null) => {
        setDate(dtMoment);
        updateValue(dtMoment);
    };

    ///
    useEffect(() => {
        var mDate = value == null ? null : moment(value);
        if (mDate === null || mDate.isValid()) {
            setDate(mDate);
        }
    }, [value]);

    ///
    useEffect(() => {
        if (valid != undefined) {
            // Para não alterar o valor diferente para campos dateOnly é verificado se a data é diferente.
            // isso porque ao formatar a data é modificado o valor de (value) deixando o valor diferente do JSON vindo do backend.
            var checkValue = value == null ? null : moment(value);
            var isEqual = (date == null && checkValue == null) || date?.isSame(checkValue);
            if (!isEqual) onChange(date == null ? null : formatDateTime(date.toDate(), timePartHour), valid);
        }
    }, [valid]);

    ///
    return (
        <FormControl margin={margin} fullWidth={fullWidth}>
            <DatePicker
                openTo="day"
                disablePast={disablePast}
                label={label}
                disabled={disabled}
                autoFocus={autoFocus}
                minDate={minDate != undefined ? moment(minDate) : undefined}
                maxDate={maxDate != undefined ? moment(maxDate) : undefined}
                views={views}
                format={format}
                slotProps={{
                    textField: {
                        required: required,
                        placeholder: placeholder,
                        title: title,
                        style: { width: fullWidth ? undefined : width },
                        onBlur: onBlur,
                        error: valid === false || error,
                        helperText: helperText,
                    },
                    field: { clearable }
                }}
                value={date}
                onChange={(newValue) => handleUpdateDate(newValue)}
                onError={(error, value) => {
                    setValid(error === null);
                    onError && onError(error, value?.toDate() ?? null);
                }}
                sx={color == undefined
                    ? undefined
                    : (theme) => ({
                        color: theme.palette[color].main,
                        svg: { color: theme.palette[color].main },
                        label: { color: theme.palette[color].main },
                        '& .MuiInput-underline:before': { borderBottomColor: theme.palette[color].main },
                        input: {
                            '&::placeholder': {
                                color: theme.palette[color].main,
                                opacity: 1,
                            },
                        }
                    })}
            />
        </FormControl>
    );
};
